// GroupsBreakdownTable.jsx
//
// Таблиця садочків одного району (або всього міста) з розгортанням до груп
// конкретного садочка — аналог KindergartenBreakdownTable.jsx для вкладки
// "Заявки на вступ". Дані сюди приходять вже обчисленими через проп `rows`;
// компонент лише фільтрує/сортує/розгортає, без власного API-виклику.
import React, { useState, useMemo } from 'react';
import { useDragScroll } from './hooks/useDragScroll';
import { useIsMobile } from './hooks/useIsMobile';
import { matchesNumericFilter, describeFilter, validateFilter } from './utils/parseNumericFilter';
import MobileSheet from './MobileSheet';

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column)
    return <span style={{ color: '#aaa', fontSize: '0.7rem', marginLeft: 4 }}>↕</span>;
  return <span style={{ color: '#0069ff', fontSize: '0.7rem', marginLeft: 4 }}>
    {sortConfig.direction === 'asc' ? '↑' : '↓'}
  </span>;
};

// Відмінювання іменників після числівників (українська)
const pluralUk = (n, one, few, many) => {
  const abs = Math.abs(n) % 100;
  const mod10 = abs % 10;
  if (abs >= 11 && abs <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
};

const baseCols = [
  { key: 'zdo',           label: 'ЗДО',               type: 'text',   placeholder: 'пошук…' },
  { key: 'groupsCount',   label: 'Кількість груп',     type: 'number', placeholder: '>0' },
  { key: 'totalPlaces',   label: 'Місць (факт)',        type: 'number', placeholder: '>0' },
  { key: 'plannedPlaces', label: 'Місць (план)',        type: 'number', placeholder: '>0' },
  { key: 'children',      label: 'Дітей',              type: 'number', placeholder: '>0' },
  { key: 'kyiv',          label: 'Киян',               type: 'number', placeholder: '>0' },
  { key: 'reserve',       label: 'Резерву',            type: 'number', placeholder: '>0' },
  { key: 'free',          label: 'Вільних',            type: 'number', placeholder: '>0' },
  { key: 'difficulties',  label: 'Труднощі',           type: 'text',   placeholder: 'пошук…' },
  { key: 'ageCategories', label: 'Вікові категорії',   type: 'text',   placeholder: 'пошук…' },
];

const districtCol = { key: 'district', label: 'Район', type: 'text', placeholder: 'пошук…' };

const filterInputStyle = {
  width: '100%',
  border: '1px solid #c8d8ff',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: '0.8rem',
  background: '#fff',
  color: '#333',
  boxSizing: 'border-box',
};

const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const buildEmptyFilters = () => {
  const f = { district: '' };
  baseCols.forEach(c => { f[c.key] = ''; });
  return f;
};

const GroupsBreakdownTable = ({ rows, disabilities, loading, showDistrict = false, scopeLabel = 'районі', capHeight = true }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState(buildEmptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [openDetailId, setOpenDetailId] = useState(null);

  const isMobile = useIsMobile();
  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();

  const cols = useMemo(
    () => (showDistrict ? [baseCols[0], districtCol, ...baseCols.slice(1)] : baseCols),
    [showDistrict]
  );

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const handleColumnFilter = (col, value) => {
    setColumnFilters(prev => ({ ...prev, [col]: value }));
  };

  const clearFilters = () => setColumnFilters(buildEmptyFilters());

  const processedData = useMemo(() => {
    let list = [...rows];

    cols.forEach(({ key, type }) => {
      if (!columnFilters[key]) return;
      if (type === 'number') list = list.filter(r => matchesNumericFilter(r[key], columnFilters[key]));
      else list = list.filter(r => String(r[key]).toLowerCase().includes(columnFilters[key].toLowerCase()));
    });

    if (sortConfig.key) {
      list.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        const cmp = typeof aVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal), 'uk');
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [rows, cols, columnFilters, sortConfig]);

  const filteredTotals = useMemo(() => {
    if (!processedData.length) return null;
    const t = { groupsCount: 0, totalPlaces: 0, plannedPlaces: 0, children: 0, kyiv: 0, reserve: 0, free: 0 };
    processedData.forEach(r => {
      t.groupsCount += r.groupsCount; t.totalPlaces += r.totalPlaces;
      t.plannedPlaces += r.plannedPlaces; t.children += r.children;
      t.kyiv += r.kyiv; t.reserve += r.reserve; t.free += r.free;
    });
    return t;
  }, [processedData]);

  const hasActiveFilters = Object.values(columnFilters).some(v => v !== '');

  const openDetailRow = useMemo(
    () => processedData.find(r => r.id === openDetailId) || null,
    [processedData, openDetailId]
  );

  if (!rows.length) {
    return (
      <div className="text-muted" style={{ fontSize: '0.85rem', padding: '14px 16px' }}>
        {loading ? 'Завантаження садочків…' : 'Немає садочків за обраними параметрами'}
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 16px' }}>
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <div style={{ fontSize: '0.82rem', color: '#666' }}>
          Садочків у {scopeLabel}: {rows.length}
          {processedData.length < rows.length && ` · показано ${processedData.length}`}
        </div>
        <div className="d-flex gap-2 align-items-center">
          {hasActiveFilters && (
            <span className="badge bg-primary" style={{ fontSize: '0.72rem' }}>
              Фільтри активні: {Object.values(columnFilters).filter(v => v !== '').length}
            </span>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            onClick={() => setShowFilters(p => !p)}
          >
            {showFilters ? '✕ Сховати фільтри' : '⚙ Фільтри по колонках'}
          </button>
          {hasActiveFilters && (
            <button
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: '0.75rem' }}
              onClick={clearFilters}
            >
              Скинути
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div style={{ background: '#f0f4ff', border: '1px solid #c8d8ff', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '0.76rem', color: '#555' }}>
          💡 Текстові поля: пошук по частині рядка. Числові поля: <code>{'>' + '10'}</code>, <code>{'<' + '50'}</code>, <code>{'5-20'}</code>, <code>{'!=' + '5'}</code>, <code>{'>2 && <5'}</code>, <code>{'<3 || >10'}</code>. Некоректний або суперечливий вираз підсвічується червоним.
        </div>
      )}

      <div
        ref={scrollRef}
        className={`publicstatistic-table-responsive ${capHeight ? 'publicstatistic-table-responsive--capped' : ''}`}
        style={{ cursor: 'grab', overflowX: 'auto', border: '1px solid #e5e7eb' }}
        {...dragListeners}
      >
        <table className="table publicstatistic-statistics-table" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              {cols.map(({ key, label }) => (
                <th key={key} style={thStyle} onClick={() => handleSort(key)}>
                  {label} <SortIcon column={key} sortConfig={sortConfig} />
                </th>
              ))}
            </tr>
            {showFilters && (
              <tr style={{ background: '#f0f4ff' }}>
                <td></td>
                {cols.map(({ key, placeholder, type }) => {
                  const check = type === 'number' && columnFilters[key] ? validateFilter(columnFilters[key]) : { valid: true };
                  return (
                    <td key={key} style={{ padding: '4px 6px', minWidth: type === 'text' ? 130 : 90, verticalAlign: 'top' }}>
                      <input
                        style={{
                          ...filterInputStyle,
                          borderColor: !check.valid ? '#dc2626' : (columnFilters[key] ? '#0069ff' : '#c8d8ff'),
                          background: !check.valid ? '#fff5f5' : filterInputStyle.background,
                        }}
                        value={columnFilters[key]}
                        onChange={e => handleColumnFilter(key, e.target.value)}
                        placeholder={placeholder}
                      />
                      {type === 'number' && columnFilters[key] && (
                        <div style={{ fontSize: '0.65rem', color: check.valid ? '#0069ff' : '#dc2626', marginTop: 2, lineHeight: 1.2 }}>
                          {describeFilter(columnFilters[key])}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            )}
            {filteredTotals && (
              <tr style={{ background: '#eef2ff', borderBottom: '2px solid #c7d2fe' }}>
                <td></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'left', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>Разом:</strong></td>
                {showDistrict && <td></td>}
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.groupsCount.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.totalPlaces.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.plannedPlaces.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.children.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.kyiv.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.reserve.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.free.toLocaleString()}</strong></td>
                <td colSpan="2"></td>
              </tr>
            )}
          </thead>
          <tbody>
            {processedData.map((row, index) => (
              <React.Fragment key={row.id}>
                <tr
                  className={index % 2 === 0 ? 'publicstatistic-row-even' : ''}
                  onClick={() => setOpenDetailId(prev => prev === row.id ? null : row.id)}
                  style={{
                    cursor: 'pointer',
                    borderLeft: openDetailId === row.id ? '3px solid #0069ff' : '3px solid transparent',
                    background: openDetailId === row.id ? '#f0f4ff' : undefined,
                  }}
                  title="Натисніть для перегляду груп"
                >
                  <td>
                    <span style={{ fontSize: '0.7rem', color: openDetailId === row.id ? '#0069ff' : '#9ca3af', marginRight: 4 }}>
                      {openDetailId === row.id ? '▼' : '▶'}
                    </span>
                    {index + 1}
                  </td>
                  <td className="publicstatistic-district-name">{row.zdo}</td>
                  {showDistrict && <td className="publicstatistic-district-name">{row.district}</td>}
                  <td>{row.groupsCount}</td>
                  <td>{row.totalPlaces.toLocaleString()}</td>
                  <td>{row.plannedPlaces.toLocaleString()}</td>
                  <td className={row.children > row.totalPlaces ? 'text-danger fw-bold' : ''}>
                    {row.children.toLocaleString()}
                  </td>
                  <td>{row.kyiv.toLocaleString()}</td>
                  <td>{row.reserve.toLocaleString()}</td>
                  <td className={row.free > 0 ? 'text-success' : (row.free < 0 ? 'text-danger' : '')}>
                    {row.free.toLocaleString()}
                  </td>
                  <td>{row.difficulties}</td>
                  <td>
                    <span className="publicstatistic-cell-clamp-2" title={row.ageCategories}>
                      {row.ageCategories}
                    </span>
                  </td>
                </tr>
                {openDetailId === row.id && !isMobile && (
                  <tr>
                    <td
                      colSpan={1 + cols.length}
                      style={{ padding: 0, background: '#f8faff', borderLeft: '3px solid #0069ff' }}
                    >
                      <GroupInlineDetail item={row._item} onClose={() => setOpenDetailId(null)} disabilities={disabilities} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {isMobile && (
        <MobileSheet
          isOpen={!!openDetailId}
          onClose={() => setOpenDetailId(null)}
        >
          {openDetailRow && (
            <GroupInlineDetail item={openDetailRow._item} onClose={() => setOpenDetailId(null)} disabilities={disabilities} />
          )}
        </MobileSheet>
      )}
    </div>
  );
};

// ── Inline детализация групп ──────────────────────────────────────────────────
const GroupInlineDetail = ({ item, onClose, disabilities }) => {
  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();

  const groups = item.groups.filter(g => g.type !== 15);

  return (
    <div style={{ padding: '12px 16px' }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>
            Групи ЗДО № {item.number}
          </span>
          <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.82rem' }}>{item.district}</span>
          <span style={{
            marginLeft: 10, display: 'inline-block', padding: '2px 10px',
            background: '#eef2ff', borderRadius: 10,
            color: '#1e40af', fontWeight: 600, fontSize: '0.78rem',
          }}>
            {groups.length} {pluralUk(groups.length, 'група', 'групи', 'груп')}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: '#f1f3f4', border: 'none', borderRadius: '50%',
            width: 30, height: 30, cursor: 'pointer', fontSize: 15,
            color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
          title="Закрити"
        >✕</button>
      </div>

      {/* Таблица групп */}
      <div
        ref={scrollRef}
        style={{ overflowX: 'auto', cursor: 'grab', borderRadius: 8, border: '1px solid #e5e7eb' }}
        {...dragListeners}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={thS}>#</th>
              <th style={{ ...thS, textAlign: 'left' }}>Група</th>
              <th style={{ ...thS, textAlign: 'left' }}>Труднощі</th>
              <th style={{ ...thS, textAlign: 'left' }}>Вікова категорія</th>
              <th style={thS}>Місць (факт)</th>
              <th style={thS}>Місць (план)</th>
              <th style={thS}>Дітей</th>
              <th style={thS}>Резерв</th>
              <th style={thS}>Вільних</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((grp, idx) => {
              let childrenCount = 0;
              if (grp.info && grp.info.length > 0)
                grp.info.forEach(ch => { childrenCount += ch.numb_ch; });

              let totalPlaces = 0;
              Object.values(grp.numberSite).forEach(p => { totalPlaces += p; });

              let plannedPlaces = 0;
              if (grp.planedSite && Object.keys(grp.planedSite).length > 0)
                Object.values(grp.planedSite).forEach(p => { plannedPlaces += p; });

              const reservCount = grp.reserv?.length || 0;
              const free = totalPlaces - childrenCount - reservCount;
              const overloaded = childrenCount + reservCount > totalPlaces;

              const difficultyLabel = grp.disabilitiesType
                ? (disabilities.find(d => d._id === grp.disabilitiesType)?.label
                    || disabilities.find(d => d._id === grp.disabilitiesType)?.name
                    || grp.disabilitiesType)
                : null;

              return (
                <tr
                  key={grp.groupId || idx}
                  style={{
                    borderBottom: '1px solid #f1f3f4',
                    background: overloaded ? '#fff5f5' : idx % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                >
                  <td style={tdC}>{idx + 1}</td>
                  <td style={tdL}>
                    <span style={{ fontWeight: 500 }}>{grp.number}</span>
                  </td>
                  <td style={{ ...tdL, color: difficultyLabel ? '#1f2937' : '#9ca3af', fontStyle: difficultyLabel ? 'normal' : 'italic' }}>
                    {difficultyLabel || 'без труднощів'}
                  </td>
                  <td style={tdL}>{grp.typeName || '—'}</td>
                  <td style={tdC}>{totalPlaces}</td>
                  <td style={{ ...tdC, color: '#6b7280' }}>{plannedPlaces || '—'}</td>
                  <td style={{ ...tdC, fontWeight: overloaded ? 700 : 400, color: overloaded ? '#dc2626' : '#1f2937' }}>
                    {childrenCount}
                  </td>
                  <td style={{ ...tdC, color: reservCount > 0 ? '#d97706' : '#9ca3af' }}>
                    {reservCount || '—'}
                  </td>
                  <td style={{ ...tdC, fontWeight: 600, color: free > 0 ? '#16a34a' : free < 0 ? '#dc2626' : '#6b7280' }}>
                    {free > 0 ? `+${free}` : free}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#f8f9fa', borderTop: '2px solid #e5e7eb' }}>
              <td colSpan={4} style={{ ...tdC, fontWeight: 600, textAlign: 'left', paddingLeft: 10 }}>Разом:</td>
              <td style={{ ...tdC, fontWeight: 600 }}>
                {groups.reduce((s, g) => s + Object.values(g.numberSite).reduce((a, b) => a + b, 0), 0)}
              </td>
              <td style={tdC}>—</td>
              <td style={{ ...tdC, fontWeight: 600 }}>
                {groups.reduce((s, g) => s + (g.info || []).reduce((a, ch) => a + ch.numb_ch, 0), 0)}
              </td>
              <td style={{ ...tdC, fontWeight: 600 }}>
                {groups.reduce((s, g) => s + (g.reserv?.length || 0), 0)}
              </td>
              <td style={{ ...tdC, fontWeight: 600 }}>
                {groups.reduce((s, g) => {
                  const places = Object.values(g.numberSite).reduce((a, b) => a + b, 0);
                  const children = (g.info || []).reduce((a, ch) => a + ch.numb_ch, 0);
                  const reserv = g.reserv?.length || 0;
                  return s + places - children - reserv;
                }, 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const thS = { padding: '8px 10px', fontWeight: 600, color: '#374151', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '2px solid #e5e7eb' };
const tdC = { padding: '7px 10px', textAlign: 'center', verticalAlign: 'middle' };
const tdL = { padding: '7px 10px', textAlign: 'left', verticalAlign: 'middle' };

export default GroupsBreakdownTable;
